import { Location } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { regexValidator } from 'src/app/helpers/customFormValidators';
import { DataService } from 'src/app/services/data/data.service';
import { LocationService } from 'src/app/services/location/location.service';
import { MapService } from 'src/app/services/map/map.service';
import { LocationStoreService } from 'src/app/stores/location-store/location-store.service';

@Component({
  selector: 'app-create-project',
  templateUrl: './create.component.html',
  styleUrls: ['./create.component.sass']
})
export class CreateComponent implements OnInit, OnDestroy, AfterViewInit {
  public form: FormGroup
  parentSelection = []
  parentSelected
  locationServiceCreateSub: Subscription;
  locationServiceSub: Subscription;

  isGetCoor: boolean
  currZoomLevel: number = 0

  /** This is a variable to be assigned with disable map click function */
  _disableMapClick: () => void

  get formName() {
    return this.form.controls['name']
  }
  get formDescription() {
    return this.form.controls['description']
  }
  get formCoor() {
    return this.form.controls['coordinate']
  }
  get formVisibility() {
    return this.form.controls['visibility']
  }

  locAdditionalData = []

  constructor(
    private formBuilder: FormBuilder,
    private spinner: NgxSpinnerService,
    private router: Router,
    private location: Location,
    private locationService: LocationService,
    private dataService: DataService,
    private toastr: ToastrService,
    private mapService: MapService,
    private _t: TranslateService,
    private locationStore: LocationStoreService
  ) { }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      name: ['', Validators.required],
      description: ['', Validators.required],
      coordinate: new FormControl('', [
        Validators.required,
        regexValidator(/^[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?),\s*[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?)$/, 'coordinateFormat'),
      ]),
      // longitude: ['', Validators.required],
      // latitude: ['', Validators.required],
      default_zoom_level: '',
      parent: '',
      visibility: ['private', Validators.required],
      additional_data_key: '',
      additional_data_val: '',
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const map = this.mapService.map
      this.currZoomLevel = map.getZoom().toFixed(3)
      map.on('move', e => {this.currZoomLevel = map.getZoom().toFixed(3)})
    });
  }

  ngOnDestroy() {
    if (this.locationServiceCreateSub) this.locationServiceCreateSub.unsubscribe()
    if (this.locationServiceSub) this.locationServiceSub.unsubscribe()
  }

  onSubmit() {
    if (this.form.invalid) {

      return this.toastr.error(this._t.instant('Please fill in required fields'))
    }
    const vertikaliti = localStorage.getItem('vertikaliti')
    this.form.value.vertikaliti = vertikaliti
    const form = this.form.value
    if (this.parentSelected) {
      form.parent = this.parentSelected.id
    } else {
      delete form.parent
    }
    form['coordinate'] = (this.form.controls['coordinate'].value as string).replace(/\s/g, "").split(',')

    // validate coordinate
    if (form['coordinate'][0] > 180 || form['coordinate'][0] < -180 || form['coordinate'][1] > 180 || form['coordinate'][1] < -180) {
      return this.toastr.error(this._t.instant('The coordinate are in decimal degrees format and range from -90 to 90 for latitude and -180 to 180 for longitude. Please provide correctly format coordinate.'))
    }
    form['additional_data_json'] = JSON.stringify(this.locAdditionalData)

    this.spinner.show();
    this.locationServiceCreateSub = this.locationService.create(form).subscribe(res => {
      if (!this.parentSelected) {
        this.locationStore.pushLocations = res
      }
      this.spinner.hide();
      this.router.navigate(['/projects', res.id, 'details', 'summary'])
    }, ({ error }) => {
      
      this.spinner.hide();
      const msg = error.message.reduce((results, u) => (results += u + ', ', results), '').slice(0, -2)
      this.toastr.error(this._t.instant(msg), this._t.instant('Error'));
    })
  }

  onDiscard() {
    if (this.location.getState() === 1) {
      this.router.navigate(['/'])
    } else {
      this.location.back()
    }
  }

  searchParent($event) {
    if ($event.target.value.length > 0) {
      this.locationServiceSub = this.locationService.findAll([
        { key: 'limit', val: 5 },
        { key: 'filtered[0][key]', val: 'name' },
        { key: 'filtered[0][type]', val: 'contains' },
        { key: 'filtered[0][val]', val: $event.target.value }
      ]).subscribe(res => {
        this.parentSelection = res.data
      })
    } else {
      this.parentSelection = []
    }
  }

  selectedParent(parent) {
    this.parentSelected = parent
    this.form.patchValue({
      parent: this.parentSelected.name
    })
    this.parentSelection = []
  }

  /**
   * Retrieves map Lnglat and fill the related fields
   */
  getMapLngLat() {
    const unsub = new Subject()

    this.toggleCoordinate()

    const res = this.mapService.enableMapCLick()
    const { click$, disableFn } = res

    this._disableMapClick = disableFn

    click$
      .pipe(takeUntil(unsub.asObservable()))
      .subscribe((evt) => {
        const { lngLat } = evt

        if (!lngLat) return

        this.form.patchValue({
          coordinate: lngLat.lng.toFixed(6) + ', ' + lngLat.lat.toFixed(6)
        })
        this.disableMapClick()

        unsub.next()
        unsub.unsubscribe()
      })
  }

  /**
   * Toggles the coordinate input state, together with its button
   */
  toggleCoordinate() {
    const control = this.form.controls['coordinate']

    if (control.enabled) {
      this.isGetCoor = true
      control.disable()
    }
    else {
      this.isGetCoor = false
      control.enable()
    }
  }

  /** Disables map click with the function provided from mapService.enableMapClick */
  disableMapClick() {
    this._disableMapClick()
    this.toggleCoordinate()
  }

  setDefaultZoom(value): void {
    this.form.patchValue({
      default_zoom_level: value
    });
  }

  addAD() {
    const locAdditionalDatum = {
      key: this.form.controls.additional_data_key.value,
      value: this.form.controls.additional_data_val.value
    }

    this.locAdditionalData.push(locAdditionalDatum)

    this.form.controls.additional_data_key.setValue('')
    this.form.controls.additional_data_val.setValue('')
  }

  removeAD(idx) {
    this.locAdditionalData.splice(idx, 1)
  }
}
